import { useCallback, useContext, useMemo, useState, useEffect } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import {
    convertEmptyStringToNull,
    convertReactSelectObjectToValue,
    validateUniqueness,
} from '../../../../../../utils/reporting/config'
import GenericTable from '../table/GenericTable'
import SelectColumnsTableViewOnly from './SelectColumnsTableViewOnly'
import { ReportingContext } from '../../../../../wrappers/ReportingContext'
import { SelectColumnsContext } from './SelectColumnsContext'
import { InputFieldColumnSimple } from '../InputField'
import ReportSelect from '../../../../select/ReportSelect'
import {
    aggregateFunctionOptions,
    translateFunctionOptions,
} from '../../../../select/StaticSelectObjects'
import { FunctionArguments } from './Arguments'
import deepEqual from 'deep-equal'
import { noBracketsRegex } from '../../../../../../utils/reporting/reportValidations'

const SelectColumns = [
    {
        Header: 'Source.Column',
        accessor: `sourceColumn`,
        Cell: ({
            cell,
            value: initialValue,
            row: { index },
            column: { id, accessor },
        }) => {
            const { control, register } = useFormContext()
            const formattedValue =
                initialValue &&
                `${initialValue.split('.')[0]}.${initialValue.split('.')[1]}`
            return (
                <>
                    {initialValue && (
                        <span>
                            {initialValue.split('.')[0]}.
                            <b>{initialValue.split('.')[1]}</b>
                        </span>
                    )}
                    {/* register an invisible input to allow for react hook form value tracking on a viewOnly field */}
                    <input
                        name={`columns[${index}].sourceColumn`}
                        ref={register()}
                        disabled
                        className={`collapsedField`}
                        defaultValue={formattedValue}
                    />
                </>
            )
        },
    },
    {
        Header: 'Column Type',
        accessor: 'dataType',
        Cell: ({
            cell,
            value: initialValue,
            row: { index },
            column: { id, accessor },
        }) => {
            const { control, register } = useFormContext()
            return (
                <>
                    {initialValue}
                    {/* register an invisible input to allow for react hook form value tracking on a viewOnly field */}
                    <input
                        name={`columns[${index}].dataType`}
                        ref={register()}
                        disabled
                        className={`collapsedField`}
                        defaultValue={initialValue}
                    />
                </>
            )
        },
    },
    {
        Header: 'Column Alias',
        accessor: 'alias',
        Cell: ({
            cell,
            value: initialValue,
            row: { index },
            rows,
            column: { id, accessor },
        }) => {
            const { register, watch, control, setValue } = useFormContext()

            const sourceColumn = useMemo(
                () => watch(`columns[${index}].sourceColumn`),
                [watch, index]
            )

            const translateFunction = useMemo(
                () =>
                    convertEmptyStringToNull(
                        convertReactSelectObjectToValue(
                            watch(`columns[${index}].function`)
                        )
                    ),
                [watch, index]
            )
            const aggregateFunction = useMemo(
                () =>
                    convertEmptyStringToNull(
                        convertReactSelectObjectToValue(
                            watch(`columns[${index}].aggregateFunction`)
                        )
                    ),
                [watch, index]
            )
            const allValues = useMemo(() => watch({ nest: true }), [watch])
            const allColumns = useMemo(() => allValues.columns, [allValues])
            const allAliases = useMemo(() => {
                return Array.isArray(allColumns) && allColumns.length
                    ? allColumns.map((v) => v.alias)
                    : []
            }, [allColumns])

            const thereAreDuplicatedSourceColumn = useMemo(() => {
                const listOfSourceCols =
                    Array.isArray(rows) &&
                    rows.length &&
                    cell &&
                    rows
                        .filter(
                            (f) =>
                                f.original.sourceColumn.split('.')[1] ===
                                cell.row.original.sourceColumn.split('.')[1]
                        )
                        .map((x) => x.original.sourceColumn.split('.')[1])

                return (
                    listOfSourceCols &&
                    listOfSourceCols.length !== new Set(listOfSourceCols).size
                )
            }, [allColumns, sourceColumn])

            return (
                <InputFieldColumnSimple
                    name={`columns[${index}].alias`}
                    // viewOnly={viewOnly || !getColumnNameValue(watch, index)}
                    inputProps={{
                        ...{
                            type: 'text',
                            ref: register({
                                pattern: {
                                    value: noBracketsRegex, // Regex that matches strings without [ or ]
                                    message:
                                        'Bracket characters are not allowed', // Error message
                                },
                                required: {
                                    value:
                                        (translateFunction !== null &&
                                            translateFunction !== undefined) ||
                                        (aggregateFunction !== null &&
                                            aggregateFunction !== undefined) ||
                                        thereAreDuplicatedSourceColumn,
                                    message: 'Required field',
                                },
                                validate: (v) =>
                                    validateUniqueness(v, allAliases),
                            }),
                            defaultValue: cell.value,
                        },
                    }}
                />
            )
        },
    },
    {
        Header: 'Translate Function',
        accessor: 'function',
        Cell: ({
            cell,
            value: initialValue,
            row: { index },
            column: { id, accessor },
        }) => {
            const { control, setValue } = useFormContext()
            return (
                <ReportSelect
                    name={`columns[${index}].function`}
                    control={control}
                    rules={{}}
                    options={translateFunctionOptions}
                    value={convertReactSelectObjectToValue(initialValue)}
                    onClear={() => {
                        setValue(`columns[${index}].functionArgument`, '')
                    }}
                    onOptionChange={(v) => {
                        setValue(`columns[${index}].functionArgument`, '')
                    }}
                />
            )
        },
    },
    {
        Header: 'Translate Argument',
        accessor: 'functionArgument',
        Cell: ({
            cell,
            value: initialValue,
            row: { index },
            column: { id, accessor },
        }) => {
            const { register, watch, control, setValue } = useFormContext()
            return (
                <FunctionArguments
                    index={index}
                    register={register}
                    watch={watch}
                    viewOnly={false}
                    control={control}
                    initialValue={initialValue}
                    setValue={setValue}
                />
            )
        },
    },
    {
        Header: 'Aggregate Function',
        accessor: 'aggregateFunction',
        Cell: ({
            cell,
            value: initialValue,
            row: { original, index },
            column: { id, accessor },
        }) => {
            const { control } = useFormContext()
            const options = aggregateFunctionOptions.filter((x) => {
                const { dataType } = original
                if (dataType === 'bit') {
                    return x.value.includes('COUNT')
                }

                if (dataType.includes('date') || dataType.includes('time')) {
                    return x.value !== 'SUM' && x.value !== 'AVG'
                }

                return true
            })

            return (
                <ReportSelect
                    name={`columns[${index}].aggregateFunction`}
                    control={control}
                    rules={{}}
                    options={options}
                    value={convertReactSelectObjectToValue(initialValue)}
                />
            )
        },
    },
]

const SelectColumnsTable = () => {
    const { formState, setValue, watch } = useFormContext()
    const { fields, append, remove, move } = useFieldArray({
        name: 'columns', // unique name for your Field Array
    })
    const { resetForm, reportConfig } = useContext(ReportingContext)
    const {
        setRightTableData,
        rightTableHighlights,
        setRightTableHighlights,
        setRightTableFunctions,
    } = useContext(SelectColumnsContext)
    const [formDirty, setFormDirty] = useState(formState.dirty)

    useEffect(() => {
        //initialize table add and remove functions
        setRightTableFunctions((prev) => {
            return { ...prev, append: append, remove: remove }
        })
    }, [append, remove, setRightTableFunctions])

    useEffect(() => {
        setRightTableData(fields)
    }, [fields, setRightTableData])

    const [moveMode, setMoveMode] = useState(false)
    useEffect(() => {
        const fieldsWithoutId = fields.map(({ id, ...rest }) => rest)
        const hasMovedColumns = !deepEqual(
            fieldsWithoutId,
            reportConfig.columns
        )

        if (moveMode && hasMovedColumns) {
            setValue('columns', fieldsWithoutId, { shouldDirty: true })
        }

        const shouldMarkDirty =
            (!moveMode && formState.dirty) || (moveMode && hasMovedColumns)

        setFormDirty(shouldMarkDirty)
    }, [fields, moveMode, formState.dirty, reportConfig.columns, setValue])

    const onMoveToggle = useCallback(
        (moveEnabled, setMoveEnabled, setShowConfirmationModal) => {
            if (formDirty) {
                setShowConfirmationModal(true)
            } else {
                setMoveEnabled((prev) => !prev)
                setMoveMode((prev) => !prev)
            }
        },
        [setMoveMode, formDirty]
    )

    return (
        <>
            {!moveMode ? (
                <GenericTable
                    columns={SelectColumns}
                    fields={fields}
                    tableHeight={'52vh'}
                    tableTile={'SELECT Columns'}
                    viewOnly={false}
                    onHightlight={setRightTableHighlights}
                    highlightedRows={rightTableHighlights}
                    filterable={false}
                    onMoveToggle={onMoveToggle}
                    formProps={{
                        formState,
                        resetForm,
                    }}
                    noDataMessage="No columns selected; the report will include all columns from all sources."
                />
            ) : (
                <GenericTable
                    columns={SelectColumnsTableViewOnly}
                    fields={fields}
                    tableHeight={'52vh'}
                    tableTile={'SELECT Columns'}
                    viewOnly={true}
                    onHightlight={setRightTableHighlights}
                    highlightedRows={rightTableHighlights}
                    filterable={false}
                    onMove={move}
                    onMoveToggle={onMoveToggle}
                    formProps={{
                        formState,
                        formDirty,
                        resetForm,
                    }}
                    noDataMessage="No columns selected; the report will include all columns from all sources."
                />
            )}
        </>
    )
}

export default SelectColumnsTable
